import * as React from "react";
import { Deployment } from "../../../schema/types/workload/deployment";
import { Workload } from "../../../schema/types/workload/entity";
import { useAppSelector } from "../../../store/hooks";
import { selectContext } from "../../../store/contextSlice";
import { Location } from "../../../schema/types/location/entity";
import { homeLink, linksOf, request, resourceLink } from "../../../services/cpln";
import { parseLink } from "../../../utils/linkParser/linkParser";
import { LogsPage, LogsFilter, FilterOption, FilterSyncConfig } from "../../../newcomponents/logs";

interface Props {
  workload: Workload;
  deployments: Deployment[];
}

export const WorkloadLogs: React.FC<Props> = ({ workload, deployments }) => {
  const { org } = useAppSelector(selectContext);
  const gvc = parseLink(linksOf(workload).self!, { useInputCtx: true }).gvc;
  const logsEndpoint = useAppSelector((state) => state.discovery.endpoints.logs);
  const grafanaEndpoint = useAppSelector((state) => state.discovery.endpoints.grafana);

  const DEFAULT_LOG_QUERY = `{gvc="${gvc}", workload="${workload.name}"}`;
  const LOGS_STORAGE_KEY = `logs-${org}-${gvc}-${workload.id}`;

  // Page-specific state: Filter dropdowns
  const [gvcLocationLinks, setGVCLocationLinks] = React.useState<string[]>([]);
  const [orgLocations, setOrgLocations] = React.useState<Location[]>([]);
  const [versionSelect, setVersionSelect] = React.useState("any");
  const [locationSelect, setLocationSelect] = React.useState("any");
  const [containerSelect, setContainerSelect] = React.useState("any");

  // Container options based on workload containers
  const containerOptions: FilterOption[] = React.useMemo(() => {
    return [
      { label: "Any Container", value: "any" },
      { label: "Access Log", value: "_accesslog" },
      { label: "Alerts", value: "_alerts" },
      { label: "Request Log", value: "_requestlog" },
      ...workload.spec.containers.map((container) => ({ label: container.name, value: container.name })),
      { label: "Custom", value: "custom" },
    ];
  }, [workload]);

  // Version options based on deployments
  const versionOptions: FilterOption[] = React.useMemo(() => {
    let maxExpectedDeploymentVersion = Math.max(...deployments.map((d) => d.status?.expectedDeploymentVersion || 0));

    const options: FilterOption[] = [{ label: "Any Version", value: "any" }];

    const versions = new Array(workload.version)
      .fill(0)
      .map((_, index) => index + 1)
      .filter((value) => value <= maxExpectedDeploymentVersion)
      .reverse()
      .slice(0, 20);

    versions.forEach((value, index) => {
      options.push({
        label: index === 0 ? String(value) + " (Latest)" : String(value),
        value: String(value),
      });
    });

    options.push({ label: "Custom", value: "custom" });

    return options;
  }, [workload.version, deployments]);

  // Location options with GVC grouping
  const locationOptions: FilterOption[] = React.useMemo(() => {
    const options: FilterOption[] = [{ label: "Any Location", value: "any" }];

    const disabledLocationLinks = orgLocations.filter((l) => !l.spec?.enabled).map((l) => linksOf(l).self!);

    const gvcLocations = orgLocations.filter((l) => {
      const selfLink = linksOf(l).self!;
      return gvcLocationLinks.includes(selfLink);
    });
    const disabledLocations = orgLocations.filter((l) => !l.spec?.enabled);
    const otherLocations = orgLocations.filter((l) => {
      const selfLink = linksOf(l).self!;
      return !gvcLocationLinks.includes(selfLink) && !disabledLocationLinks.includes(selfLink);
    });

    gvcLocations.forEach((loc) => {
      options.push({ label: loc.name, value: loc.name, group: "GVC" });
    });

    otherLocations.forEach((loc) => {
      options.push({ label: loc.name, value: loc.name, group: "Other" });
    });

    disabledLocations.forEach((loc) => {
      options.push({ label: loc.name, value: loc.name, group: "Disabled", disabled: true });
    });

    options.push({ label: "Custom", value: "custom" });
    return options;
  }, [orgLocations, gvcLocationLinks]);

  // Fetch org and GVC locations
  React.useEffect(() => {
    async function setOrgGVCLocations() {
      try {
        let orgLocationLink: string | undefined = homeLink("location");
        let locations: Location[] = [];
        while (orgLocationLink) {
          const { data } = await request({ url: orgLocationLink });
          locations.push(...data.items);
          orgLocationLink = linksOf(data).next;
        }
        setOrgLocations(locations);
      } catch (e) {
        console.error("failed to set org locations");
      }
      try {
        let gvcLink: string | undefined = resourceLink("gvc", gvc!);
        const { data } = await request({ url: gvcLink! });
        setGVCLocationLinks(data.spec.staticPlacement?.locationLinks || []);
      } catch (e) {
        console.error("failed to set gvc location links");
      }
    }
    setOrgGVCLocations();
  }, [gvc]);

  // Filter sync config - LogsPage handles bidirectional sync
  const filters: FilterSyncConfig[] = React.useMemo(
    () => [
      {
        key: "location",
        value: locationSelect,
        onChange: setLocationSelect,
        options: locationOptions,
      },
      {
        key: "container",
        value: containerSelect,
        onChange: setContainerSelect,
        options: containerOptions,
      },
      {
        key: "version",
        value: versionSelect,
        onChange: setVersionSelect,
        options: versionOptions,
      },
    ],
    [locationSelect, locationOptions, containerSelect, containerOptions, versionSelect, versionOptions]
  );

  // Filters slot (UI only)
  const filtersSlot = (
    <>
      <LogsFilter
        label="Location"
        value={locationSelect}
        options={locationOptions}
        onChange={setLocationSelect}
        placeholder="Select location"
      />
      <LogsFilter
        label="Container"
        value={containerSelect}
        options={containerOptions}
        onChange={setContainerSelect}
        placeholder="Select container"
      />
      <LogsFilter
        label="Version"
        value={versionSelect}
        options={versionOptions}
        onChange={setVersionSelect}
        placeholder="Select version"
      />
    </>
  );

  return (
    <LogsPage
      org={org!}
      defaultQuery={DEFAULT_LOG_QUERY}
      storageKey={LOGS_STORAGE_KEY}
      fileNamePrefix={workload.name}
      logsEndpoint={logsEndpoint}
      grafanaEndpoint={grafanaEndpoint.replace("{org}", org!)}
      filterSlot={filtersSlot}
      filters={filters}
    />
  );
};
